import { useQuery } from '@tanstack/react-query'
import { api } from '../../services/api'
import { Badge, Card, EmptyState, Spinner } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { CLINIC } from '../../config/clinic'
import { ConfirmAppointmentButton } from './ConfirmAppointmentButton'
import { FOLLOW_UP_STATUS, formatDate } from '../../utils/format'

interface PortalFollowUp {
  id: string
  suggestedDate: string
  intervalLabel?: string | null
  reason?: string | null
  status: string
  professional?: { firstName: string; lastName: string; specialty?: string | null } | null
  service?: { name: string } | null
  appointment?: { id: string; startsAt: string; status: string } | null
}

/**
 * Enlace de WhatsApp con el mensaje ya escrito: el paciente solo aprieta
 * enviar. Agendar en línea entra cuando la agenda pública esté abierta al
 * paciente; por ahora el canal es WhatsApp/teléfono, como pidió el cliente.
 */
function scheduleLink(followUp: PortalFollowUp): string {
  const text = `Hola, quiero agendar mi control${followUp.reason ? ` (${followUp.reason})` : ''} indicado para el ${formatDate(
    followUp.suggestedDate,
  )}.`
  return `https://wa.me/${CLINIC.whatsappNumber}?text=${encodeURIComponent(text)}`
}

/** Próximos controles indicados por el profesional (P2). */
export function MisControlesPage() {
  const followUps = useQuery({
    queryKey: ['patient-follow-ups'],
    queryFn: async () => (await api.get<PortalFollowUp[]>('/patient-portal/follow-ups')).data,
  })

  const list = followUps.data ?? []
  const today = new Date().toISOString().slice(0, 10)

  return (
    <Card title="Próximos controles">
      {followUps.isLoading ? (
        <Spinner />
      ) : list.length === 0 ? (
        <EmptyState message="No tienes controles pendientes indicados por tu médico." />
      ) : (
        <div className="space-y-2">
          {list.map((f) => {
            const due = f.suggestedDate.slice(0, 10) <= today && f.status === 'PENDING'
            return (
              <div
                key={f.id}
                className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${
                  due ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                }`}
              >
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium text-ink">
                    {f.reason ?? 'Control médico'}
                    <Badge className={FOLLOW_UP_STATUS[f.status]?.className}>{FOLLOW_UP_STATUS[f.status]?.label}</Badge>
                  </p>
                  <p className="text-sm text-gray-600">
                    Fecha sugerida: {formatDate(f.suggestedDate)}
                    {f.intervalLabel ? ` (${f.intervalLabel} después de tu atención)` : ''}
                  </p>
                  {f.professional && (
                    <p className="text-xs text-gray-500">
                      Indicado por {f.professional.firstName} {f.professional.lastName}
                      {f.professional.specialty ? ` · ${f.professional.specialty}` : ''}
                    </p>
                  )}
                  {f.appointment && (
                    <p className="text-xs text-emerald-700">Ya tienes hora para el {formatDate(f.appointment.startsAt)}.</p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap items-start gap-2">
                  {/* Control ya agendado: lo que falta es que el paciente
                      confirme que irá. Sin hora todavía, las vías siguen
                      siendo WhatsApp y teléfono. */}
                  {f.appointment && (
                    <ConfirmAppointmentButton
                      appointmentId={f.appointment.id}
                      status={f.appointment.status}
                      startsAt={f.appointment.startsAt}
                    />
                  )}
                  {f.status === 'PENDING' && (
                    <>
                      <a
                        href={scheduleLink(f)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                      >
                        <Icon name="whatsapp" className="h-4 w-4" />
                        Agendar
                      </a>
                      <a
                        href={`tel:${CLINIC.phone}`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-steel-300 bg-white px-3 py-2 text-sm font-medium text-ink hover:bg-soft"
                      >
                        <Icon name="phone" className="h-4 w-4" />
                        Llamar
                      </a>
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
      {/* TODO-NEGOCIO: teléfono y WhatsApp definitivos (config/clinic.ts). */}
      <p className="mt-3 text-xs text-gray-400">
        Para agendar te contactamos por WhatsApp o teléfono; si tu control ya tiene hora, confírmala desde aquí. La
        reserva en línea desde el portal llega en una próxima versión.
      </p>
    </Card>
  )
}
